/**
 * Slug helpers + URL builders.
 *
 * Service+city slugs combine service slug + "-" + city slug, e.g.:
 *   "car-transport" + "-" + "london" → "car-transport-london"
 *
 * Some city slugs contain hyphens (e.g. "stoke-on-trent"), so naive string-
 * splitting will not work. We exhaustively check known service slugs as a
 * prefix and treat the remainder as the city slug.
 */
import { SERVICES, SERVICE_LIST } from "../data/services";
import { CITIES, CITY_LIST } from "../data/cities";

// Sorted longest-first so longer service slugs match before shorter prefixes.
const SERVICE_SLUGS_BY_LENGTH = SERVICE_LIST
  .map((s) => s.slug)
  .sort((a, b) => b.length - a.length);

/**
 * Parse a URL slug like "car-transport-london" into { service, city }.
 * Returns null if the slug is not a valid service or service+city combination.
 */
export function parseSlug(slug) {
  if (!slug) return null;
  // First, exact service slug (no city suffix) — e.g. "car-transport"
  if (SERVICES[slug]) {
    return { service: SERVICES[slug], city: null };
  }
  // Otherwise look for any service slug as a prefix followed by "-" + a city slug.
  for (const serviceSlug of SERVICE_SLUGS_BY_LENGTH) {
    const prefix = `${serviceSlug}-`;
    if (slug.startsWith(prefix)) {
      const citySlug = slug.slice(prefix.length);
      const city = CITIES[citySlug];
      if (city) {
        return { service: SERVICES[serviceSlug], city };
      }
    }
  }
  return null;
}

export function urlForService(service) {
  return `/${service.slug}`;
}

export function urlForServiceCity(service, city) {
  return `/${service.slug}-${city.slug}`;
}

export function allServiceCityCombinations() {
  const out = [];
  for (const service of SERVICE_LIST) {
    for (const city of CITY_LIST) {
      out.push({ service, city, url: urlForServiceCity(service, city) });
    }
  }
  return out;
}

export function allServicePages() {
  return SERVICE_LIST.map((service) => ({ service, url: urlForService(service) }));
}
